import * as React from 'react';

import { ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';

export type ComposerState = {
  recipients: { [uuid: string]: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity };
  filters: { [recipientUuid: string]: { [filterType: string]: string[] } };
}

export type ComposerDispatchAction = 
  | { type: 'addRecipient', entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity }
  | { type: 'removeRecipient', uuid: string }
  | { type: 'addFilter', recipientUuid: string, filterType: string, filterValue: string }
  | { type: 'removeFilter', recipientUuid: string, filterType: string, filterValue: string }
  | { type: 'removeAllFilters', recipientUuid: string }
  | { type: 'removeAllRecipients' };

export type ComposerDispatch = (action: ComposerDispatchAction) => void;

const defaultState: ComposerState = {
  recipients: {},
  filters: {},
};

export const ComposerStateContext = React.createContext<ComposerState>(defaultState);
export const ComposerDispatchContext = React.createContext<ComposerDispatch>(() => {});

function composerReducer(state: ComposerState, action: ComposerDispatchAction) {
  switch (action.type) {
    case 'addRecipient': {
      const recipients = state.recipients;
      recipients[action.entity.recipient.uuid] = action.entity;
      return { ...state, recipients };
    } 
    case 'removeRecipient': {
      const recipients = state.recipients;
      delete recipients[action.uuid];

      const filters = state.filters;
      delete filters[action.uuid];

      return { ...state, recipients, filters };
    }
    case 'addFilter': {
      const filters = state.filters;

      if (!filters[action.recipientUuid]) {
        filters[action.recipientUuid] = {};
      }
      
      if (!filters[action.recipientUuid][action.filterType]) {
        filters[action.recipientUuid][action.filterType] = [];
      }
      
      filters[action.recipientUuid][action.filterType].push(action.filterValue);
      
      return { ...state, filters };
    }
    case 'removeFilter': {
      const filters = state.filters;
      const recipientFilters = filters[action.recipientUuid][action.filterType];
      recipientFilters.splice(recipientFilters.indexOf(action.filterValue), 1);
      return { ...state, filters };
    }
    case 'removeAllFilters': {
      const filters = state.filters;
      delete filters[action.recipientUuid];
      return { ...state, filters };
    }
    case 'removeAllRecipients': {
      return { ...state, recipients: {}, filters: {} };
    }
    default: 
      return state;
  }
}

export function ComposerContextProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(composerReducer, defaultState);

  return (
    <ComposerStateContext.Provider value={state}>
      <ComposerDispatchContext.Provider value={dispatch}>
        {children}
      </ComposerDispatchContext.Provider>
    </ComposerStateContext.Provider>
  )
}

export function withComposerDispatch(Component: React.ComponentType<any>) {
  return (props: any) => {
    return (
      <ComposerDispatchContext.Consumer>
        {dispatch => <Component {...props} composerDispatch={dispatch} />}
      </ComposerDispatchContext.Consumer>
    )
  }
}

export function withComposerState(Component: React.ComponentType<any>) {
  return (props: any) => {
    return (
      <ComposerStateContext.Consumer>
        {state => <Component {...props} composerState={state} />}
      </ComposerStateContext.Consumer>
    )
  }
}
